import { useCallback, useEffect, useMemo, useState } from "react";

import { toast } from "components/ui/use-toast";
import {
  useBlockMonthMutation,
  useGetMonthControlsQuery,
  useReleaseMonthMutation,
} from "@/services/tax-closing-period";

import { MONTHS_PT } from "../constants";
import {
  type DialogAction,
  type MonthStats,
  type MonthView,
  getMonthYear,
} from "../types";
import {
  MONTH_STAT_KEY_BY_STATUS,
  MONTH_STAT_KEY_BY_VIEW_KIND,
  TAX_CLOSING_PERIOD_STATUS,
  TAX_CLOSING_PERIOD_VIEW_KIND,
} from "../status";
import { PERIOD_GATE_TOASTS } from "../messages";

export function usePeriodGate() {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [dialogAction, setDialogAction] = useState<DialogAction>(null);
  const [dialogTarget, setDialogTarget] = useState<MonthView | null>(null);
  const [observation, setObservation] = useState("");

  const {
    data: months,
    isLoading,
    isError,
    refetch,
  } = useGetMonthControlsQuery();
  const [releaseMonth, { isLoading: isReleasing }] = useReleaseMonthMutation();
  const [blockMonth, { isLoading: isBlocking }] = useBlockMonthMutation();

  // Merge API data with 12 placeholders for the selected year
  const monthViews = useMemo<MonthView[]>(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const monthNum = i + 1;
      const real = months?.find(
        (m) => m.month === monthNum && m.year === selectedYear,
      );
      if (real) return { kind: TAX_CLOSING_PERIOD_VIEW_KIND.REAL, data: real };
      return {
        kind: TAX_CLOSING_PERIOD_VIEW_KIND.PLACEHOLDER,
        month: monthNum,
        year: selectedYear,
      };
    });
  }, [months, selectedYear]);

  // Stats for header badges (only for selected year)
  const stats = useMemo<MonthStats>(() => {
    const initialStats: MonthStats = {
      released: 0,
      blocked: 0,
      closed: 0,
      placeholder: 0,
    };

    return monthViews.reduce<MonthStats>((acc, view) => {
      let statKey: keyof MonthStats;
      if (view.kind === TAX_CLOSING_PERIOD_VIEW_KIND.PLACEHOLDER) {
        // Meses futuros sem cadastro contam como "Fechado" (mesmo limite
        // de isFuture usado no MonthGrid: > mês atual + 2).
        const isFuture =
          selectedYear > currentYear ||
          (selectedYear === currentYear && view.month > currentMonth + 2);
        statKey = isFuture ? "closed" : MONTH_STAT_KEY_BY_VIEW_KIND[view.kind];
      } else {
        statKey = MONTH_STAT_KEY_BY_STATUS[view.data.status];
      }

      return {
        ...acc,
        [statKey]: acc[statKey] + 1,
      };
    }, initialStats);
  }, [monthViews, selectedYear, currentYear, currentMonth]);

  // Recent history entries — derive from releasedAt on existing records
  const recentHistory = useMemo(() => {
    if (!months) return [];
    return [...months]
      .filter((m) => m.releasedAt)
      .sort(
        (a, b) =>
          new Date(b.releasedAt!).getTime() -
          new Date(a.releasedAt!).getTime(),
      )
      .slice(0, 4);
  }, [months]);

  // Years that have any data, for the mini multi-year heatmap
  const yearsWithData = useMemo(() => {
    if (!months || months.length === 0) return [currentYear];
    const set = new Set<number>(months.map((m) => m.year));
    set.add(currentYear);
    return Array.from(set)
      .sort((a, b) => b - a)
      .slice(0, 4);
  }, [months, currentYear]);

  // Keyboard arrows change year (but not while a dialog is open or focused on a textarea)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (dialogAction) return;
      if (e.target && (e.target as HTMLElement).tagName === "TEXTAREA") return;
      if (e.key === "ArrowLeft") setSelectedYear((y) => y - 1);
      if (e.key === "ArrowRight") setSelectedYear((y) => y + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialogAction]);

  const openDialog = useCallback((view: MonthView) => {
    if (
      view.kind === TAX_CLOSING_PERIOD_VIEW_KIND.REAL &&
      view.data.status === TAX_CLOSING_PERIOD_STATUS.CLOSED
    ) return;

    const isReleased =
      view.kind === TAX_CLOSING_PERIOD_VIEW_KIND.REAL &&
      view.data.status === TAX_CLOSING_PERIOD_STATUS.RELEASED;
    setDialogTarget(view);
    setDialogAction(isReleased ? "block" : "release");
    setObservation("");
  }, []);

  const closeDialog = useCallback(() => {
    setDialogAction(null);
    setDialogTarget(null);
    setObservation("");
  }, []);

  const handleRelease = useCallback(async () => {
    if (!dialogTarget) return;
    const { month, year } = getMonthYear(dialogTarget);
    try {
      await releaseMonth({
        month,
        year,
        observation: observation || undefined,
      }).unwrap();
      toast(PERIOD_GATE_TOASTS.releaseSuccess(MONTHS_PT[month - 1], year));
      closeDialog();
    } catch (err) {
      const description =
        (err as { data?: { message?: string } })?.data?.message ?? "Erro desconhecido";
      toast(PERIOD_GATE_TOASTS.releaseError(description));
    }
  }, [dialogTarget, observation, releaseMonth, closeDialog]);

  const handleBlock = useCallback(async () => {
    if (!dialogTarget) return;
    const { month, year } = getMonthYear(dialogTarget);
    try {
      await blockMonth({ month, year }).unwrap();
      toast(PERIOD_GATE_TOASTS.blockSuccess(MONTHS_PT[month - 1], year));
      closeDialog();
    } catch (err) {
      const description =
        (err as { data?: { message?: string } })?.data?.message ?? "Erro desconhecido";
      toast(PERIOD_GATE_TOASTS.blockError(description));
    }
  }, [dialogTarget, blockMonth, closeDialog]);

  return {
    // server state
    months,
    isLoading,
    isError,
    refetch,
    // mutation state
    isReleasing,
    isBlocking,
    // year nav
    currentYear,
    currentMonth,
    selectedYear,
    setSelectedYear,
    // derived data
    monthViews,
    stats,
    recentHistory,
    yearsWithData,
    // dialog state
    dialogAction,
    dialogTarget,
    observation,
    setObservation,
    openDialog,
    closeDialog,
    handleRelease,
    handleBlock,
  };
}
